import { ApolloClient } from '@apollo/client'

import {
  FinanceFactSummariesDocument,
  FinanceFactSummariesQuery,
  FinancialWorkspaceValueObjectsCostTypeEnum,
  FinancialWorkspaceValueObjectsFinanceTypeEnum,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import SafeJSON from 'modules/core/lib/SafeJSON'

import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'

import { transformItemToRecord } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/transformRecords'
import {
  FINANCE_TYPE_IDS,
  getResourceFilterKey,
  isExpensesRow,
} from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/expanderHelpers'

interface UseFetchChildrenOptions {
  client: ApolloClient<object>
  groupBy: InsightTableGroupBy
  search?: string
  formattedStartDate: string
  formattedEndDate: string
  effectiveCostType: FinancialWorkspaceValueObjectsCostTypeEnum
  // Serialized filter keys for stable dependency tracking
  legalEntityIdsKey: string
  locationIdsKey: string
  costCenterIdsKey: string
  employeeIdsKey: string
  financeTypesKey: string
}

/**
 * Hook that returns a callback to fetch children for FinanceFactSummaries (Employee Costs / Compensations)
 *
 * For non-Total views: Uses groupBy: Total with resource filter to get breakdown
 * For Total view:
 *   - Expenses: Uses groupBy: CustomCategory to get expense category breakdown
 *   - Payroll/Compensation: Uses groupBy: LineItem to get line item breakdown
 */
export const useFetchChildrenForFinanceFactSummaries = ({
  client,
  groupBy,
  search,
  formattedStartDate,
  formattedEndDate,
  effectiveCostType,
  legalEntityIdsKey,
  locationIdsKey,
  costCenterIdsKey,
  employeeIdsKey,
  financeTypesKey,
}: UseFetchChildrenOptions) => {
  return useUnsafeCallback(
    async (item: FinancialWorkspaceRecord): Promise<FinancialWorkspaceRecord[]> => {
      // If children are already pre-loaded, return them directly
      if (item.children && item.children.length > 0) {
        return item.children
      }

      // Determine the filter key based on current groupBy
      const resourceFilterKey = getResourceFilterKey(groupBy)

      // Parse the stable keys back to arrays for the query
      const legalEntityIds = SafeJSON.parse<number[]>(legalEntityIdsKey, [])
      const locationIds = SafeJSON.parse<number[]>(locationIdsKey, [])
      const costCenterIds = SafeJSON.parse<number[]>(costCenterIdsKey, [])
      const employeeIds = SafeJSON.parse<number[]>(employeeIdsKey, [])
      const financeTypesArray = SafeJSON.parse<FinancialWorkspaceValueObjectsFinanceTypeEnum[]>(
        financeTypesKey,
        []
      )

      // Determine the groupBy for children based on current view and item type
      let childrenGroupBy: InsightTableGroupBy
      let childFinanceTypes: FinancialWorkspaceValueObjectsFinanceTypeEnum[]

      // Check if the item being expanded is an Expenses row
      // This can happen in two scenarios:
      // 1. Top-level Total view with Expenses row
      // 2. Non-Total view where we expanded a resource and got Expenses as a child
      if (isExpensesRow(item)) {
        // Expenses rows expand to show CustomCategory breakdown
        childrenGroupBy = InsightTableGroupBy.CustomCategory
        childFinanceTypes = ['expense']
      } else if (groupBy === InsightTableGroupBy.Total) {
        // For Total view with non-Expenses rows, they shouldn't be expandable
        // (Payroll/Agreements don't expand in Total view)
        return []
      } else {
        // For non-Total views (Cost Center, Employee, etc.)
        // Fetch with groupBy: Total to get Payroll/Expenses/Agreements breakdown for this resource
        childrenGroupBy = InsightTableGroupBy.Total
        childFinanceTypes = financeTypesArray
      }

      // Build variables for children query
      const emptyStringArray: string[] = []
      const childrenVariables = {
        formattedStartDate,
        formattedEndDate,
        groupBy: childrenGroupBy,
        financeTypes: childFinanceTypes,
        customCategoryIds: [],
        managerIds: emptyStringArray,
        legalEntityIds,
        locationIds,
        costCenterIds,
        employeeIds,
        search: search ?? undefined,
        excludeChildrenData: true, // Children are leaf nodes, no further expansion
        costType: effectiveCostType,
        itemsPerPage: 100, // Fetch all children at once
      }

      // Override the filter to scope to the parent item (for non-Total views)
      // Only apply when expanding a resource row (not when expanding an Expenses row)
      if (resourceFilterKey && !isExpensesRow(item)) {
        Object.assign(childrenVariables, { [resourceFilterKey]: [String(item.id)] })
      }

      try {
        const result = await client.query<FinanceFactSummariesQuery>({
          query: FinanceFactSummariesDocument,
          variables: childrenVariables,
          fetchPolicy: 'network-only',
        })

        const edges = result.data.financialWorkspace.financeFactSummariesConnection.edges

        // Determine if these children are leaf nodes or can be further expanded
        // - When expanding an Expenses row: children are CustomCategory results (leaf nodes)
        // - When expanding a resource row: children are finance type rows (Expenses can expand)
        const isLeafLevel = isExpensesRow(item)

        return edges.map((edge) => {
          // For non-leaf levels, Expenses children can be expanded further
          const childCanExpand = !isLeafLevel && edge.node.id === FINANCE_TYPE_IDS.EXPENSES

          return transformItemToRecord({
            id: edge.node.id,
            label: edge.node.label,
            rows: edge.node.rows,
            children: [],
            categoryLabel: edge.node.categoryLabel,
            category: edge.node.category,
            active: edge.node.active,
            imageUrl: edge.node.imageUrl,
            hasChildren: childCanExpand,
            isChild: !childCanExpand, // Only true leaf nodes are marked as isChild
          })
        })
      } catch {
        return []
      }
    },
    [
      client,
      groupBy,
      search,
      formattedStartDate,
      formattedEndDate,
      legalEntityIdsKey,
      locationIdsKey,
      costCenterIdsKey,
      employeeIdsKey,
      financeTypesKey,
    ]
  )
}
